import type { HonuaErrorCategory, HonuaErrorCode, HonuaErrorDomain } from "./error-code-registry.js";

/**
 * The classification state required by the error base at runtime.
 *
 * Human-readable summaries intentionally live only in
 * `error-code-registry.ts`. Keep this table private to SDK internals and use
 * `check:error-codes` to prove exact parity with the canonical registry.
 *
 * @internal
 */
export type HonuaErrorRuntimeClassification = readonly [
  domain: HonuaErrorDomain,
  category: HonuaErrorCategory,
  retryable: boolean,
];

/** @internal */
export const HONUA_ERROR_RUNTIME_CLASSIFICATIONS = Object.freeze({
  "core.http.transient": runtimeClassification("core", "protocol", true),
  "core.http.rejected": runtimeClassification("core", "protocol", false),
  "core.timeout": runtimeClassification("core", "timeout", true),
  "core.network": runtimeClassification("core", "network", true),
  "core.cancelled": runtimeClassification("core", "cancellation", false),
  "core.grpc.transient": runtimeClassification("core", "protocol", true),
  "core.grpc.rejected": runtimeClassification("core", "protocol", false),
  "pmtiles.lifecycle.invalid-request": runtimeClassification("pmtiles", "validation", false),
  "pmtiles.lifecycle.invalid-response": runtimeClassification("pmtiles", "validation", false),
  "pmtiles.lifecycle.response-too-large": runtimeClassification("pmtiles", "validation", false),
  "pmtiles.lifecycle.job-poll-timeout": runtimeClassification("pmtiles", "timeout", true),
  "pmtiles.lifecycle.job-failed": runtimeClassification("pmtiles", "protocol", false),
  "pmtiles.lifecycle.job-cancelled": runtimeClassification("pmtiles", "cancellation", false),
  "pmtiles.lifecycle.access-url-expired": runtimeClassification("pmtiles", "validation", false),
  "pmtiles.lifecycle.manual-cleanup-unsupported": runtimeClassification("pmtiles", "capability", false),
  "core.geometry.unknown-geometry": runtimeClassification("core", "validation", false),
  "core.geometry.malformed-geometry": runtimeClassification("core", "validation", false),
  "core.auth.interaction-required": runtimeClassification("core", "authentication", false),
  "core.auth.refresh-failed": runtimeClassification("core", "authentication", true),
  "core.auth.invalid-grant": runtimeClassification("core", "authentication", false),
  "core.capability-not-supported": runtimeClassification("core", "capability", false),
  "core.exploration-context": runtimeClassification("core", "validation", false),
  "core.wfs-exception": runtimeClassification("core", "protocol", false),
  "core.job-failed": runtimeClassification("core", "protocol", false),
  "core.wms-capabilities-parse": runtimeClassification("core", "protocol", false),
  "core.wmts-capabilities-parse": runtimeClassification("core", "protocol", false),
  "core.coverage.invalid-request": runtimeClassification("core", "validation", false),
  "core.coverage.invalid-response": runtimeClassification("core", "protocol", false),
  "core.coverage.response-too-large": runtimeClassification("core", "validation", false),
  "core.coverage.unsupported-format": runtimeClassification("core", "capability", false),
  "core.coverage.service-error": runtimeClassification("core", "protocol", false),
  "core.coverage.wcs-exception": runtimeClassification("core", "protocol", false),
  "core.zarr.invalid-request": runtimeClassification("core", "validation", false),
  "core.zarr.invalid-response": runtimeClassification("core", "protocol", false),
  "core.zarr.response-too-large": runtimeClassification("core", "validation", false),
  "core.zarr.metadata-pending": runtimeClassification("core", "capability", false),
  "core.zarr.missing-spatial-extent": runtimeClassification("core", "capability", false),
  "core.zarr.no-tileable-variable": runtimeClassification("core", "capability", false),
  "core.zarr.missing-spatial-reference": runtimeClassification("core", "capability", false),
  "core.zarr.spatial-reference-mismatch": runtimeClassification("core", "capability", false),
  "core.zarr.unsupported-version": runtimeClassification("core", "capability", false),
  "core.zarr.unsupported-codec": runtimeClassification("core", "capability", false),
  "core.zarr.unsupported-dtype": runtimeClassification("core", "capability", false),
  "core.zarr.ambiguous-dimensions": runtimeClassification("core", "validation", false),
  "core.zarr.service-error": runtimeClassification("core", "protocol", false),
  "discovery.ambiguous-protocol": runtimeClassification("discovery", "validation", false),
  "discovery.ambiguous-source": runtimeClassification("discovery", "validation", false),
  "discovery.invalid-cloud-native-input": runtimeClassification("discovery", "validation", false),
  "discovery.invalid-cloud-native-manifest": runtimeClassification("discovery", "validation", false),
  "discovery.invalid-endpoint": runtimeClassification("discovery", "validation", false),
  "discovery.invalid-cache-identity": runtimeClassification("discovery", "validation", false),
  "discovery.invalid-discovery-cache": runtimeClassification("discovery", "validation", false),
  "discovery.invalid-capability": runtimeClassification("discovery", "validation", false),
  "discovery.cloud-native-operation-unavailable": runtimeClassification("discovery", "capability", false),
  "discovery.unsupported-protocol": runtimeClassification("discovery", "capability", false),
  "discovery.protocol-mismatch": runtimeClassification("discovery", "validation", false),
  "query.planning.invalid-query": runtimeClassification("query", "validation", false),
  "query.planning.unsupported-compiler": runtimeClassification("query", "capability", false),
  "query.planning.unsupported-query": runtimeClassification("query", "capability", false),
  "query.planning.capability-not-supported": runtimeClassification("query", "capability", false),
  "query.planning.fallback-disabled": runtimeClassification("query", "capability", false),
  "query.planning.unsafe-materialization": runtimeClassification("query", "validation", false),
  "query.execution.invalid-plan": runtimeClassification("query", "validation", false),
  "query.execution.wfs-protocol": runtimeClassification("query", "protocol", false),
  "query.execution.plan-context-mismatch": runtimeClassification("query", "validation", false),
  "query.execution.unsafe-materialization": runtimeClassification("query", "validation", false),
  "query.execution.invalid-resource-handle": runtimeClassification("query", "validation", false),
  "query.execution.resource-unavailable": runtimeClassification("query", "authentication", false),
  "query.execution.resource-expired": runtimeClassification("query", "authentication", false),
  "query.execution.resource-resolution-failed": runtimeClassification("query", "internal", false),
  "query.execution.resource-execution-failed": runtimeClassification("query", "internal", false),
  "map.source-adapter.disposed": runtimeClassification("map", "validation", false),
  "map.source-adapter.source-conflict": runtimeClassification("map", "validation", false),
  "map.source-adapter.layer-conflict": runtimeClassification("map", "validation", false),
  "map.source-adapter.unsupported-plan": runtimeClassification("map", "capability", false),
  "map.source-adapter.invalid-option": runtimeClassification("map", "validation", false),
  "map.source-adapter.map-mutation-failed": runtimeClassification("map", "internal", false),
  "map.data-bridge.invalid-option": runtimeClassification("map", "validation", false),
  "map.data-bridge.disposed": runtimeClassification("map", "validation", false),
  "map.data-bridge.source-conflict": runtimeClassification("map", "validation", false),
  "map.data-bridge.layer-conflict": runtimeClassification("map", "validation", false),
  "map.data-bridge.map-mutation-failed": runtimeClassification("map", "internal", false),
  "map.data-bridge.interaction-unsupported": runtimeClassification("map", "capability", false),
  "map.data-bridge.filter-unsupported": runtimeClassification("map", "capability", false),
  "map.automatic-strategy.no-eligible-strategy": runtimeClassification("map", "capability", false),
  "map.automatic-strategy.stale-plan": runtimeClassification("map", "validation", false),
  "map.automatic-strategy.source-conflict": runtimeClassification("map", "validation", false),
  "map.automatic-strategy.layer-conflict": runtimeClassification("map", "validation", false),
  "map.automatic-strategy.map-mutation-failed": runtimeClassification("map", "internal", false),
  "map.automatic-strategy.cancelled": runtimeClassification("map", "cancellation", false),
  "map.automatic-strategy.disposed": runtimeClassification("map", "validation", false),
  "map.raster-strategy.unsupported-strategy": runtimeClassification("map", "capability", false),
  "map.raster-strategy.capability-mismatch": runtimeClassification("map", "capability", false),
  "map.raster-strategy.missing-metadata": runtimeClassification("map", "validation", false),
  "map.raster-strategy.invalid-option": runtimeClassification("map", "validation", false),
  "map.raster-strategy.source-conflict": runtimeClassification("map", "validation", false),
  "map.raster-strategy.layer-conflict": runtimeClassification("map", "validation", false),
  "map.raster-strategy.map-mutation-failed": runtimeClassification("map", "internal", false),
  "map.automatic-integration.disposed": runtimeClassification("map", "validation", false),
  "map.automatic-integration.invalid-target": runtimeClassification("map", "validation", false),
  "map.temporal-playback.invalid-option": runtimeClassification("map", "validation", false),
  "runtime.map-package.fetch": runtimeClassification("runtime", "network", true),
  "runtime.map-package.load": runtimeClassification("runtime", "internal", false),
  "runtime.map-package.validate": runtimeClassification("runtime", "validation", false),
  "runtime.map-package.export": runtimeClassification("runtime", "validation", false),
  "runtime.map-package.import": runtimeClassification("runtime", "validation", false),
  "runtime.map-package.update": runtimeClassification("runtime", "internal", false),
  "runtime.map-package.style-compose": runtimeClassification("runtime", "validation", false),
  "runtime.map-package.source-bind": runtimeClassification("runtime", "internal", false),
  "runtime.map-package.view": runtimeClassification("runtime", "internal", false),
  "runtime.map-package.popup": runtimeClassification("runtime", "validation", false),
  "runtime.map-package.dispose": runtimeClassification("runtime", "internal", true),
  "runtime.diagnostic": runtimeClassification("runtime", "validation", false),
  "runtime.query-tiles.transient": runtimeClassification("runtime", "protocol", true),
  "runtime.query-tiles.rejected": runtimeClassification("runtime", "protocol", false),
  "realtime.cancelled": runtimeClassification("realtime", "cancellation", false),
  "realtime.transport.reconnectable": runtimeClassification("realtime", "network", true),
  "realtime.checkpoint.invalid": runtimeClassification("realtime", "validation", false),
  "realtime.sequence.gap": runtimeClassification("realtime", "protocol", true),
  "realtime.protocol.terminal": runtimeClassification("realtime", "protocol", false),
  "realtime.reconciliation.disposed": runtimeClassification("realtime", "validation", false),
  "realtime.reconciliation.invalid-option": runtimeClassification("realtime", "validation", false),
  "offline.region.validation": runtimeClassification("offline", "validation", false),
  "offline.region.quota": runtimeClassification("offline", "validation", false),
  "offline.region.integrity": runtimeClassification("offline", "protocol", false),
  "offline.region.miss": runtimeClassification("offline", "validation", false),
  "offline.cancelled": runtimeClassification("offline", "cancellation", false),
  "offline.transport.failure": runtimeClassification("offline", "network", false),
  "offline.transport.transient": runtimeClassification("offline", "network", true),
  "offline.storage.concurrent": runtimeClassification("offline", "internal", true),
  "offline.storage.failure": runtimeClassification("offline", "internal", false),
  "offline.replica-sync.capability": runtimeClassification("offline", "capability", false),
  "offline.replica-sync.validation": runtimeClassification("offline", "validation", false),
  "offline.replica-sync.permission-denied": runtimeClassification("offline", "authentication", false),
  "plugin.registry.validation": runtimeClassification("plugin", "validation", false),
  "plugin.compatibility": runtimeClassification("plugin", "capability", false),
  "plugin.execution.policy-denied": runtimeClassification("plugin", "capability", false),
  "plugin.capability-unavailable": runtimeClassification("plugin", "capability", false),
  "plugin.lifecycle.activation": runtimeClassification("plugin", "internal", false),
  "plugin.execution.validation": runtimeClassification("plugin", "validation", false),
  "plugin.lifecycle.cleanup": runtimeClassification("plugin", "internal", false),
  "plugin.cancelled": runtimeClassification("plugin", "cancellation", false),
  "plugin.internal": runtimeClassification("plugin", "internal", false),
  "agent.tool.unknown-tool": runtimeClassification("agent", "validation", false),
  "agent.tool.missing-runtime": runtimeClassification("agent", "validation", false),
  "agent.tool.unqualified-selection": runtimeClassification("agent", "validation", false),
  "agent.tool.missing-runtime-method": runtimeClassification("agent", "capability", false),
  "agent.tool.internal": runtimeClassification("agent", "internal", false),
  "agent.safety.aborted": runtimeClassification("agent", "cancellation", false),
  "agent.safety.invalid-input": runtimeClassification("agent", "validation", false),
  "agent.safety.policy-denied": runtimeClassification("agent", "capability", false),
  "agent.safety.integrity-failed": runtimeClassification("agent", "protocol", false),
  "agent.safety.approval-expired": runtimeClassification("agent", "authentication", false),
  "agent.safety.context-mismatch": runtimeClassification("agent", "validation", false),
  "agent.safety.signature-invalid": runtimeClassification("agent", "validation", false),
  "agent.safety.execution-failed": runtimeClassification("agent", "internal", false),
  "agent.safety.audit-failed": runtimeClassification("agent", "internal", false),
  "agent.safety.receipt-failed": runtimeClassification("agent", "internal", false),
  "app.unsupported-profile": runtimeClassification("app", "capability", false),
  "app.unsupported-widget": runtimeClassification("app", "capability", false),
  "app.missing-manifest": runtimeClassification("app", "validation", false),
  "app.missing-manifest-artifact": runtimeClassification("app", "validation", false),
  "app.missing-map-package": runtimeClassification("app", "validation", false),
  "app.map-package-mismatch": runtimeClassification("app", "validation", false),
  "app.missing-widget": runtimeClassification("app", "validation", false),
  "app.missing-binding": runtimeClassification("app", "validation", false),
  "app.map-load-failed": runtimeClassification("app", "internal", false),
  "app.data-load-failed": runtimeClassification("app", "internal", false),
  "app.render-failed": runtimeClassification("app", "internal", false),
  "app.disposed": runtimeClassification("app", "validation", false),
  "app.export-unsafe": runtimeClassification("app", "validation", false),
  "app.export-failed": runtimeClassification("app", "internal", false),
} as const satisfies Record<HonuaErrorCode, HonuaErrorRuntimeClassification>);

function runtimeClassification(
  domain: HonuaErrorDomain,
  category: HonuaErrorCategory,
  retryable: boolean,
): HonuaErrorRuntimeClassification {
  return Object.freeze([domain, category, retryable]);
}
